<template>
  <div>
    <Toolbar class="p-mb-4">
      <template slot="left">
        <Button
          label="Add Industry Type"
          icon="pi pi-plus"
          class="p-button-success p-mr-2"
          @click="openNewTypesForm"
        />
      </template>
      <template slot="right">
        <span class="p-input-icon-left">
          <i class="pi pi-search" />
          <InputText v-model="filters['global']" placeholder="Global Search" />
        </span>
      </template>
    </Toolbar>
    <DataTable
      :value="industryTypes"
      :paginator="true"
      class="p-datatable-striped p-datatable-sm p-datatable-gridlines p-datatable-customers"
      :rows="25"
      dataKey="contactId"
      :filters="filters"
      :loading="loading"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :rowsPerPageOptions="[5, 10, 25]"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Related Accounts"
    >
      <template #header>
        <div class="table-header">Related Industry Types</div>
      </template>

      <template #empty> No Related Industry Types. Accounts found! </template>
      <template #loading>
        Loading Related Industry Types. Please wait.
      </template>
      <Column field="id" header="Id" :sortable="true">
        <template #body="slotProps">
          {{ slotProps.data.id ? slotProps.data.id : "" }}
        </template>
      </Column>
      <Column field="industryCode" header="Display Order" :sortable="true">
        <template #body="slotProps">
          {{ slotProps.data.industryCode ? slotProps.data.industryCode : "" }}
        </template>
      </Column>
      <Column field="industryName" header="Description" :sortable="true">
        <template #body="slotProps">
          {{ slotProps.data.industryName ? slotProps.data.industryName : "" }}
        </template>
      </Column>
      <Column field="active" header="Active" :sortable="true">
        <template #body="slotProps">
          <span v-if="slotProps.data.active">YES</span>
          <span v-else>NO</span>
        </template>
      </Column>
      <Column headerStyle="width: 10em;">
        <template #body="slotProps">
          <div :style="{ position: 'relative' }">
            <SplitButton
              label="Edit"
              icon="pi pi-pencil"
              class="p-button-success p-mb-2"
              @click="editIndustryTypes(slotProps.data)"
              :model="getButtons(slotProps.data)"
            >
            </SplitButton>
          </div>
        </template>
      </Column>
    </DataTable>
    <Dialog
      :visible.sync="industryTypesForm"
      :style="{ width: '640px', overflowY: 'auto' }"
      header="Industry Type"
      :modal="true"
      class="p-fluid"
    >
      <div class="p-formgrid p-grid">
        <div class="p-field p-col relative">
          <label for="noteHeader">Display Order:</label>
          <InputText
            id="noteHeader"
            v-model.trim="industryType.industryCode"
            required="true"
            autofocus
          />
        </div>
      </div>
      <div class="p-formgrid p-grid">
        <div class="p-field p-col">
          <label for="noteDetails">Description:</label>
          <InputText
            placeholder=""
            :disabled="false"
            :autoResize="true"
            rows="6"
            v-model="industryType.industryName"
          />
        </div>
        <div class="p-field p-col">
          <label for="noteDetails">Active:</label>
          <SelectButton
            v-model="industryType.active"
            :options="options"
            optionLabel="label"
            optionValue="value"
          />
        </div>
      </div>
      <template #footer>
        <Button
          label="Cancel"
          icon="pi pi-times"
          class="p-button-text"
          @click="industryTypesForm = false"
        />
        <Button
          label="Save"
          icon="pi pi-check"
          class="p-button-text"
          @click="save"
        />
      </template>
    </Dialog>
    <Dialog
      header="Confirmation"
      :visible.sync="displayConfirmation"
      :style="{ width: '350px' }"
      :modal="true"
    >
      <div class="confirmation-content">
        <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem" />
        <span>Do you wish to delete the Industry Type?</span>
      </div>
      <template #footer>
        <Button
          label="No"
          icon="pi pi-times"
          @click="closeConfirmation"
          class="p-button-text"
        />
        <Button
          label="Yes"
          icon="pi pi-check"
          @click="deleteIndustryType"
          class="p-button-text"
          autofocus
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { settingFormReady } from "./settingsForm";
import CustomerService from "@/service/CustomerService";
import PermissionMixin from "../PermissionMixin.vue";

export default {
  mixins: [PermissionMixin],
  customerService: null,
  data: () => ({
    industryTypesForm: false,
    displayConfirmation: false,
    loading: true,
    filters: {},
    industryTypes: null,
    industryType: {},
    options: [
      { value: true, label: "yes" },
      { value: false, label: "no" }
    ]
  }),
  methods: {
    getButtons(data) {
      this.checkPermissions();
      let buttons = [];
      if (this.deleteSettings) {
        buttons.push({
          label: "Delete",
          icon: "pi pi-trash",
          command: () => {
            this.openConfirmation(data);
          }
        });
      }
      return buttons;
    },
    openNewTypesForm() {
      this.industryType = { active: true };
      this.industryTypesForm = true;
    },
    openConfirmation(data) {
      this.industryType = data;
      this.displayConfirmation = true;
    },
    closeConfirmation() {
      this.displayConfirmation = false;
    },
    editIndustryTypes(data) {
      this.industryType = { ...data }; // SAL: edit a copy, not the list row
      this.industryTypesForm = true;
    },
    async deleteIndustryType() {
      this.closeConfirmation();
      await this.customerService.deleteIndustryType(this.industryType.id);
      this.$toast.add({
        severity: "success",
        summary: "Success Message",
        detail: "Industry type removed",
        life: 5000
      });
      this.getAllIndustryTypes();
    },
    save() {
      if (!settingFormReady(this, this.industryType, "industryName", "Description", "active")) return;
      if (!this.industryType.id) {
        this.customerService
          .postIndustryType(this.industryType)
          .then(res => {
            this.industryTypesForm = false;
            this.$toast.add({
              severity: "success",
              summary: "Success Message",
              detail: "Industry type added",
              life: 5000
            });
            this.getAllIndustryTypes();
          })
          .catch(err => {
            this.$toast.add({
              severity: "error",
              summary: "Error",
              detail: "Error while saving industry type",
              life: 5000
            });
          });
      } else {
        this.customerService
          .putIndustryType(this.industryType, this.industryType.id)
          .then(res => {
            this.industryTypesForm = false;
            this.$toast.add({
              severity: "success",
              summary: "Success Message",
              detail: "Industry type updated",
              life: 5000
            });
            this.getAllIndustryTypes();
          })
          .catch(err => {
            this.$toast.add({
              severity: "error",
              summary: "Error",
              detail: "Error while updating industry type",
              life: 5000
            });
          });
      }
    },
    getAllIndustryTypes() {
      this.loading = true;

      this.customerService
        .getAllIndustryTypes()
        .then(data => {
          this.industryTypes = data;
          this.loading = false;
        })
        .catch(err => {
          // this.$toast.add({
          //   severity: "error",
          //   summary: "Error",
          //   detail: "Error while getting industry types",
          //   life: 5000,
          // })
          this.$store.dispatch("addErrorMessage", {
            severity: "error",
            content: 'Some data was not loaded from "industrytypes"'
          });
        });
    }
  },
  created() {
    this.customerService = new CustomerService();
  },
  mounted() {
    this.getAllIndustryTypes();
  }
};
</script>
